import { useState, useEffect } from "react";
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

export default function CompleteModal({ job, operation, session, autoQuantity, onCancel, onSubmit }) {
  const [qty, setQty] = useState(job?.qty ?? "");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const totalQty = job?.qty ?? 0;

  const isAuto = !!autoQuantity;

  // Compute elapsed time (running or paused)
  const [elapsed, setElapsed] = useState("00:00:00");

  useEffect(() => {
    if (!session) return;

    function format(sec) {
      const h = String(Math.floor(sec / 3600)).padStart(2, "0");
      const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
      const s = String(Math.floor(sec % 60)).padStart(2, "0");
      return `${h}:${m}:${s}`;
    }

    function compute() {
      // Get accumulated time from previous pause/resume cycles (floor to remove decimals)
      const accumulated = Math.floor(session.accumulatedSeconds || 0);

      if (session.state === "paused") {
        // When paused, durationSeconds should already include accumulated time
        setElapsed(format(Math.floor(session.durationSeconds ?? accumulated)));
        return;
      }
      if (session.state === "running" && session.startTime) {
        const start = new Date(session.startTime).getTime();
        const now = Date.now();
        const currentSegment = Math.floor((now - start) / 1000);
        // Total time = accumulated from previous segments + current segment
        const totalSec = accumulated + currentSegment;
        setElapsed(format(totalSec));
      }
    }

    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [session]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // If auto-quantity is enabled, skip input and auto-submit qty
    if (autoQuantity) {
      onSubmit(job.qty ?? 0);
      return;
    }

    const n = Number(qty);
    if (!Number.isFinite(n) || n < 0) {
      alert("Enter a valid non-negative number.");
      return;
    }

    onSubmit(n);
  };

  // Quick submit handlers
  const handleQuickAll = () => onSubmit(totalQty);
  const handleQuickNone = () => onSubmit(0);
  const handleQuickCustom = () => setShowCustomInput(true);

  if (!job) return null;

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={`Complete Task – ${operation?.operation || "Task"} – ${elapsed}`}
      size="sm"
    >
      <p className="text-sm text-gray-500 mb-4">
        Job <span className="font-mono text-gray-900">{job.jobNumber}</span>{" "}
        — {job.name || "Unnamed Job"}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {autoQuantity ? (
          <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
            This operation auto-defaults quantity.
            <br />
            Pieces Finished = <span className="font-semibold">{job.qty}</span>
          </div>
        ) : showCustomInput ? (
          /* Custom quantity input mode */
          <div className="flex flex-col gap-3">
            <label className="text-sm text-gray-700">
              Enter quantity completed:
            </label>
            <input
              type="number"
              min="0"
              max={totalQty}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              autoFocus
              className="
                bg-white text-gray-900 border border-gray-200 rounded-lg
                px-4 py-3 text-lg font-mono
                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500
              "
            />
            <button
              type="button"
              onClick={() => setShowCustomInput(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Back to quick options
            </button>
          </div>
        ) : (
          /* Quick selection mode - three big buttons */
          <div className="space-y-3">
            <p className="text-sm text-gray-700 mb-3">
              How many pieces did you finish?
              <span className="text-gray-500 ml-2">(Total: {totalQty})</span>
            </p>

            <div className="grid grid-cols-1 gap-2">
              {/* All pieces button */}
              <button
                type="button"
                onClick={handleQuickAll}
                className="
                  w-full py-3 rounded-lg text-sm font-medium
                  bg-green-600 hover:bg-green-500 text-gray-900
                  transition-colors
                "
              >
                All of them ({totalQty} pieces)
              </button>

              {/* None button */}
              <button
                type="button"
                onClick={handleQuickNone}
                className="
                  w-full py-3 rounded-lg text-sm font-medium
                  bg-slate-100 hover:bg-gray-200 text-gray-700 border border-gray-200
                  transition-colors
                "
              >
                None (0 pieces)
              </button>

              {/* Different quantity button */}
              <button
                type="button"
                onClick={handleQuickCustom}
                className="
                  w-full py-3 rounded-lg text-sm font-medium
                  bg-blue-600 hover:bg-blue-500 text-gray-900
                  transition-colors
                "
              >
                Different quantity...
              </button>
            </div>
          </div>
        )}

        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          {autoQuantity && (
            <Button type="submit">
              Complete
            </Button>
          )}
          {showCustomInput && (
            <Button type="submit">
              Complete ({qty} pieces)
            </Button>
          )}
        </Modal.Footer>
      </form>
    </Modal>
  );
}
